import { useEffect, useState } from 'react'
import { maskCNPJInput, validateCNPJ } from '../utils/cnpj'
import { useNavigate, useParams } from 'react-router-dom'
import { adminApi, getApiErrorMessage } from '../services/adminApi'
import { useAdminAuth } from '../context/AdminAuthContext'
import { Alert, Checkbox, FormActions, FormSection, Input, PageHeader, Spinner } from '../components/ui'

const emptyForm = {
  tenant_id: '',
  company_name: '',
  cnpj: '',
  admin_email: 'admin@mamcontrol.com',
  admin_password: 'Admin@mam1',
  admin_name: 'AdminMAM',
  phone: '',
  city: '',
  state: '',
  max_pessoas: 1000,
  max_equipamentos: 50,
  module_clube: false,
  club_dias_tolerancia: 5,
  club_bloquear_inadimplente: true,
}

export default function TenantForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isRevenda } = useAdminAuth()
  const editing = Boolean(id)
  const [form, setForm] = useState(emptyForm)
  const [loading, setLoading] = useState(editing)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!editing) return
    let active = true
    async function load() {
      try {
        const { data } = isRevenda ? await adminApi.listRevendaTenants() : await adminApi.listTenants()
        const tenant = (data.data || []).find((item) => item.tenant_id === id)
        if (!tenant) throw new Error('Cliente não encontrado.')
        if (active) {
          setForm({
            ...emptyForm,
            ...tenant,
            tenant_id: tenant.tenant_id,
            company_name: tenant.company_name || '',
            cnpj: tenant.cnpj || '',
            phone: tenant.phone || '',
            city: tenant.city || '',
            state: tenant.state || '',
            max_pessoas: tenant.max_pessoas || 1000,
            max_equipamentos: tenant.max_equipamentos || 50,
            module_clube: tenant.module_clube === true || tenant.module_clube === 'true',
            club_dias_tolerancia: tenant.club_dias_tolerancia ?? 5,
            club_bloquear_inadimplente:
              tenant.club_bloquear_inadimplente !== false
              && tenant.club_bloquear_inadimplente !== 'false',
          })
        }
      } catch (err) {
        if (active) setError(getApiErrorMessage(err, 'Não foi possível carregar o cliente.'))
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => { active = false }
  }, [editing, id, isRevenda])

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }))

  async function submit(event) {
    event.preventDefault()
    if (saving) return
    if (form.cnpj?.trim() && !validateCNPJ(form.cnpj)) {
      setError('CNPJ inválido.')
      return
    }
    setSaving(true)
    setError('')
    try {
      if (editing) {
        const payload = {
          company_name: form.company_name,
          cnpj: form.cnpj,
          phone: form.phone,
          city: form.city,
          state: form.state,
          max_pessoas: Number(form.max_pessoas),
          max_equipamentos: Number(form.max_equipamentos),
          module_clube: Boolean(form.module_clube),
          club_dias_tolerancia: Number(form.club_dias_tolerancia),
          club_bloquear_inadimplente: Boolean(form.club_bloquear_inadimplente),
        }
        await adminApi.editTenant(id, payload)
      } else {
        const payload = {
          ...form,
          max_pessoas: Number(form.max_pessoas),
          max_equipamentos: Number(form.max_equipamentos),
          module_clube: Boolean(form.module_clube),
          club_dias_tolerancia: Number(form.club_dias_tolerancia),
          club_bloquear_inadimplente: Boolean(form.club_bloquear_inadimplente),
        }
        await adminApi.createTenant(payload)
      }
      navigate('/tenants', { replace: true, state: { message: editing ? 'Cliente atualizado.' : 'Cliente criado com sucesso.' } })
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Spinner label="Carregando cliente…" />

  return (
    <form onSubmit={submit} className="space-y-6">
      <PageHeader
        eyebrow="Clientes"
        title={editing ? `Editar ${id}` : 'Novo cliente'}
        description={editing ? 'Atualize os dados operacionais e limites desta base.' : 'Configure a empresa, o acesso inicial e os limites da nova base.'}
        icon="fa-building"
      />
      {error && <Alert type="error">{error}</Alert>}

      <FormSection title="Dados da empresa" description="Identificação e localização da organização." icon="fa-building-circle-check">
        <div className="grid gap-4 md:grid-cols-2">
          {!editing && <Input label="Tenant ID *" value={form.tenant_id} onChange={(e) => update('tenant_id', e.target.value)} required autoFocus hint="Identificador único usado nas rotas e integrações." />}
          <Input label="Empresa *" value={form.company_name} onChange={(e) => update('company_name', e.target.value)} required />
          <Input label="CNPJ *" value={form.cnpj} onChange={(e) => update('cnpj', maskCNPJInput(e.target.value))} required className="uppercase" placeholder="00.000.000/0001-00 ou 12.ABC.345/01DE-35" />
          <Input label="Telefone" value={form.phone} onChange={(e) => update('phone', e.target.value)} />
          <Input label="Cidade" value={form.city} onChange={(e) => update('city', e.target.value)} />
          <Input label="UF" value={form.state} maxLength={2} onChange={(e) => update('state', e.target.value.toUpperCase())} />
        </div>
      </FormSection>

      {!editing && (
        <FormSection title="Administrador inicial" description="Credenciais usadas no primeiro acesso da nova base." icon="fa-user-shield">
          <div className="grid gap-4 md:grid-cols-2">
            <Input label="Nome *" value={form.admin_name} onChange={(e) => update('admin_name', e.target.value)} required />
            <Input label="E-mail *" type="email" value={form.admin_email} onChange={(e) => update('admin_email', e.target.value)} required />
            <Input label="Senha inicial *" type="password" value={form.admin_password} onChange={(e) => update('admin_password', e.target.value)} required autoComplete="new-password" />
          </div>
        </FormSection>
      )}

      <FormSection title="Capacidade da base" description="Limites contratados para pessoas e equipamentos." icon="fa-gauge-high">
        <div className="grid gap-4 md:grid-cols-2">
          <Input label="Máximo de pessoas" type="number" min="1" value={form.max_pessoas} onChange={(e) => update('max_pessoas', e.target.value)} required />
          <Input label="Máximo de equipamentos" type="number" min="1" value={form.max_equipamentos} onChange={(e) => update('max_equipamentos', e.target.value)} required />
        </div>
      </FormSection>

      <FormSection
        title="Módulo Clube"
        description="Add-on pago que ativa preset esportivo: oculta módulos condominiais e habilita gestão de associados, planos e mensalidades."
        icon="fa-medal"
      >
        <div className="space-y-4">
          <Checkbox
            label="Ativar Módulo Clube"
            description="Aplica automaticamente tema, labels e flags de módulos para perfil de clube esportivo."
            checked={Boolean(form.module_clube)}
            onChange={(e) => update('module_clube', e.target.checked)}
          />
          {form.module_clube && (
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Dias de tolerância (inadimplência)"
                type="number"
                min="0"
                value={form.club_dias_tolerancia}
                onChange={(e) => update('club_dias_tolerancia', e.target.value)}
                hint="Dias após o vencimento antes de bloquear acesso na catraca."
              />
              <Checkbox
                label="Bloquear inadimplente na catraca"
                description="Nega acesso quando houver mensalidade vencida além da tolerância."
                checked={Boolean(form.club_bloquear_inadimplente)}
                onChange={(e) => update('club_bloquear_inadimplente', e.target.checked)}
              />
            </div>
          )}
        </div>
      </FormSection>

      <FormActions onCancel={() => navigate('/tenants')} saving={saving} submitLabel={editing ? 'Salvar alterações' : 'Criar cliente'} />
    </form>
  )
}
